/**
 * IndexedDB persistence for consult-note audio (CLA-938, Phase 1).
 *
 * Until this shipped, web had no durable storage for note audio at any point.
 * The recorded blob lived in a local variable inside `stopRecording()`; a failed
 * commit reset the store to `idle` and returned, the blob went out of scope, and
 * the consult audio was gone — no retry, no recovery, nothing on disk to recover
 * from. iOS holds an on-disk queue with ~100 minutes of retries; Android
 * persists to internal storage and re-queues orphans on launch. Web alone threw
 * the audio away. Surfaced by the 2026-08-10 transcription degradation (CLA-935):
 * that incident lost nothing, but it made the question concrete — had the API
 * itself been degraded, web clinicians would have lost consults silently.
 *
 * Deliberately a SEPARATE database from `call-recording-db.ts`. That one is the
 * telehealth call-recording queue: it appends chunks live during a call and has
 * its own schema, lifecycle and recovery pass. Consult notes are a different
 * flow (one assembled blob written once, at stop), and sharing a store would
 * have coupled two recovery passes that fail for different reasons. The idioms
 * are the same on purpose — `idb`, graceful degradation, a persisted
 * idempotency key — so anything learned on one transfers to the other.
 *
 * Every function tolerates IndexedDB being unavailable (private browsing, a
 * blocked origin, jsdom) by returning a null/empty/`ok: false` result. Losing
 * the safety net must never cost the recording itself.
 */

import { openDB as idbOpen, type IDBPDatabase } from 'idb'
import type { StoppedReason } from '@/lib/recording-ceiling'

const DB_NAME = 'claro-note-recordings'
const DB_VERSION = 1

/**
 * `pending`   — audio is on disk, a commit is owed, "Retry upload" is offered.
 * `uploading` — a commit attempt is in flight. Reverts to `pending` on failure;
 *               the row is deleted on success. A row left here is an orphan
 *               (tab closed mid-upload) and recovery treats it as `pending`.
 * `plan_capped` — the server said 402 note_cap_reached. The audio is fine, the
 *               plan is not: retrying just 402s again until the plan changes, so
 *               recovery keeps the row but does NOT offer a retry button.
 * `unreadable` — the browser can no longer read the audio back out of this row.
 *               The opposite of `plan_capped`: there the audio is fine and the
 *               plan is not; here the plan is fine and the audio is gone. Same
 *               treatment for the same reason — a retry cannot succeed, so
 *               offering one teaches people to ignore the banner that protects
 *               their consults (CLA-1243).
 */
export type PendingStatus = 'pending' | 'uploading' | 'plan_capped' | 'unreadable'

export interface PendingNoteRecording {
  id?: number
  /**
   * The recording's Idempotency-Key, minted BEFORE the first commit attempt and
   * persisted here so every later retry sends the SAME key. This is the whole
   * reason a retry is safe: a progressive commit can partially succeed (or
   * succeed with the response lost), and without a stable key the retry would
   * mint a fresh UUID and the server would write a second note row for one
   * consult. Same lesson as `call-recording-db`'s `idempotencyKey`.
   */
  idempotencyKey: string
  /** The draft note this audio belongs to (`existing_note_id` at generate). */
  noteId: number | null
  /** The doctor's "Your notes" jottings (→ `user_notes`), CLA-772. */
  userNotes: string
  /** The full assembled recording. See `getPendingBlob` for why it is full. */
  audio: Blob
  /** Denormalised so the recovery UI can size an entry without reading the blob. */
  audioSize: number
  /** Recording length in seconds, for the "12:04 consult" label in the banner. */
  durationSeconds: number
  /**
   * Why the recording stopped when it was not the clinician's own Stop — the
   * 6-hour ceiling (CLA-969) — so a retry sends the server the same
   * `stopped_reason` the first attempt did. Optional: rows written before it
   * existed have no field, and read as a normal stop. No schema bump is needed
   * for an added property on an object store.
   */
  stoppedReason?: StoppedReason | null
  status: PendingStatus
  /** Commit attempts so far (initial + retries) — surfaced in the banner. */
  attempts: number
  /** Last failure message, shown verbatim so the clinician sees the real cause. */
  lastError: string | null
  createdAt: string
  updatedAt: string
}

/**
 * Result of a persistence attempt. Deliberately a discriminated union rather
 * than `number | null`: the caller has to be able to tell "your audio is safe"
 * from "your audio is NOT safe, don't close this tab", and a bare null collapses
 * those into one silent outcome — which is the class of bug this file exists to
 * fix.
 */
export type SaveResult =
  | { ok: true; id: number }
  | { ok: false; reason: 'unavailable' | 'quota' | 'error'; message: string }

/**
 * Retention budget. A 15.4-minute consult (the longest real one at the time of
 * writing, note 17403) is ~3–4 MB of webm/opus at MediaRecorder's default
 * bitrate, so a 400 MB / 20-entry ceiling is roughly a day of unrecoverable
 * backlog — far more than Phase 1 needs, and small enough that a quota-limited
 * origin is not pushed over by us alone. Eviction is oldest-first and only ever
 * touches rows ABOVE the ceiling; the row being written is never the victim.
 */
const MAX_ENTRIES = 20
const MAX_TOTAL_BYTES = 400 * 1024 * 1024

type NoteRecordingDB = {
  pending: {
    key: number
    value: PendingNoteRecording
  }
}

let dbPromise: Promise<IDBPDatabase<NoteRecordingDB>> | null = null

function getDB(): Promise<IDBPDatabase<NoteRecordingDB>> | null {
  if (typeof indexedDB === 'undefined') {
    console.warn('[note-recording-db] IndexedDB not available')
    return null
  }
  if (!dbPromise) {
    dbPromise = idbOpen<NoteRecordingDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('pending')) {
          db.createObjectStore('pending', { keyPath: 'id', autoIncrement: true })
        }
      },
    }).catch((e) => {
      console.warn('[note-recording-db] Failed to open database:', e)
      dbPromise = null
      throw e
    })
  }
  return dbPromise
}

/** A DOMException named QuotaExceededError, however the browser spells it. */
function isQuotaError(e: unknown): boolean {
  return (
    e instanceof DOMException &&
    (e.name === 'QuotaExceededError' || e.name === 'NS_ERROR_DOM_QUOTA_REACHED')
  )
}

/**
 * Drop the oldest rows until the store is inside the retention budget, and
 * return how many were evicted. Called before every write so the budget is
 * enforced on the way in rather than discovered as a quota error later.
 *
 * `plan_capped` rows are evicted like any other: they are waiting on a billing
 * change that may never come, and holding a plan-blocked consult forever at the
 * expense of a fresh one that could actually upload is the wrong trade.
 */
async function evictBeyondBudget(db: IDBPDatabase<NoteRecordingDB>): Promise<number> {
  const all = await db.getAll('pending')
  // Oldest first, so the survivors are the newest.
  all.sort((a, b) => a.createdAt.localeCompare(b.createdAt))

  let bytes = all.reduce((n, r) => n + (r.audioSize || 0), 0)
  let count = all.length
  let evicted = 0

  for (const row of all) {
    if (count < MAX_ENTRIES && bytes <= MAX_TOTAL_BYTES) break
    if (row.id == null) continue
    await db.delete('pending', row.id)
    bytes -= row.audioSize || 0
    count -= 1
    evicted += 1
  }
  if (evicted > 0) {
    console.warn(`[note-recording-db] Evicted ${evicted} old pending recording(s) to stay in budget`)
  }
  return evicted
}

/**
 * Persist a recording and return its row id. Called from `stopRecording()`
 * BEFORE the first commit attempt — that ordering is the fix, not an
 * optimisation: persisting after a failed commit is persisting a blob that has
 * already gone out of scope.
 *
 * On QuotaExceededError it evicts the single oldest row and retries once, then
 * gives up with `reason: 'quota'`. It never throws and never returns a bare
 * null, so the caller can tell the clinician the truth about whether their audio
 * is protected.
 */
export async function savePendingRecording(entry: {
  idempotencyKey: string
  noteId: number | null
  userNotes: string
  audio: Blob
  durationSeconds: number
  stoppedReason?: StoppedReason | null
}): Promise<SaveResult> {
  const promise = getDB()
  if (!promise) {
    return { ok: false, reason: 'unavailable', message: 'Offline storage is unavailable in this browser.' }
  }

  let db: IDBPDatabase<NoteRecordingDB>
  try {
    db = await promise
  } catch (e) {
    console.warn('[note-recording-db] savePendingRecording could not open the database:', e)
    return { ok: false, reason: 'unavailable', message: 'Offline storage is unavailable in this browser.' }
  }

  const now = new Date().toISOString()
  const row: PendingNoteRecording = {
    idempotencyKey: entry.idempotencyKey,
    noteId: entry.noteId,
    userNotes: entry.userNotes,
    audio: entry.audio,
    audioSize: entry.audio.size,
    durationSeconds: entry.durationSeconds,
    stoppedReason: entry.stoppedReason ?? null,
    status: 'uploading',
    attempts: 0,
    lastError: null,
    createdAt: now,
    updatedAt: now,
  }

  try {
    await evictBeyondBudget(db)
  } catch (e) {
    // Budget enforcement is best-effort — a failure here must not stop the write.
    console.warn('[note-recording-db] eviction pass failed:', e)
  }

  try {
    const id = await db.add('pending', row)
    return { ok: true, id }
  } catch (e) {
    if (!isQuotaError(e)) {
      console.warn('[note-recording-db] savePendingRecording failed:', e)
      return { ok: false, reason: 'error', message: 'Could not save a local copy of this recording.' }
    }
    // Out of quota. Free the single oldest entry and try once more — one retry,
    // not a loop: a loop would silently shred the whole backlog to fit one
    // oversized recording, which trades one clinician's lost consult for
    // several.
    try {
      const all = await db.getAll('pending')
      all.sort((a, b) => a.createdAt.localeCompare(b.createdAt))
      const oldest = all[0]
      if (oldest?.id != null) await db.delete('pending', oldest.id)
      const id = await db.add('pending', row)
      console.warn('[note-recording-db] Quota exceeded — evicted the oldest recording to make room')
      return { ok: true, id }
    } catch (retryError) {
      console.warn('[note-recording-db] savePendingRecording out of quota:', retryError)
      return {
        ok: false,
        reason: 'quota',
        message: 'Browser storage is full, so no local backup of this recording could be saved.',
      }
    }
  }
}

/** Read one row (including its blob). Null when missing or unreadable. */
export async function getPendingRecording(id: number): Promise<PendingNoteRecording | null> {
  try {
    const promise = getDB()
    if (!promise) return null
    const db = await promise
    return (await db.get('pending', id)) ?? null
  } catch (e) {
    console.warn('[note-recording-db] getPendingRecording failed:', e)
    return null
  }
}

/**
 * All persisted recordings, newest first.
 *
 * There is no status filter here on purpose. A row left in `uploading` is not
 * healthy — it means the tab died mid-commit — so recovery has to see it too,
 * which is precisely the case `getOrphanedRecordings()` in `call-recording-db`
 * filters out.
 */
export async function getPendingRecordings(): Promise<PendingNoteRecording[]> {
  try {
    const promise = getDB()
    if (!promise) return []
    const db = await promise
    const all = await db.getAll('pending')
    return all.sort((a, b) => b.createdAt.localeCompare(a.createdAt))
  } catch (e) {
    console.warn('[note-recording-db] getPendingRecordings failed:', e)
    return []
  }
}

/** Update a row's status / error / attempt count. No-ops if the row is gone. */
export async function updatePendingStatus(
  id: number,
  status: PendingStatus,
  opts?: { lastError?: string | null; incrementAttempts?: boolean },
): Promise<void> {
  try {
    const promise = getDB()
    if (!promise) return
    const db = await promise
    const row = await db.get('pending', id)
    if (!row) return
    row.status = status
    if (opts && 'lastError' in opts) row.lastError = opts.lastError ?? null
    if (opts?.incrementAttempts) row.attempts += 1
    row.updatedAt = new Date().toISOString()
    await db.put('pending', row)
  } catch (e) {
    console.warn('[note-recording-db] updatePendingStatus failed:', e)
  }
}

/**
 * Delete a recording. Only ever called on a CONFIRMED successful commit or an
 * explicit user discard — never on an assumption that the upload probably
 * worked. (Phase 2 adopts the stricter iOS standard: delete only on proof the
 * server holds a transcribed draft.)
 */
export async function deletePendingRecording(id: number): Promise<void> {
  try {
    const promise = getDB()
    if (!promise) return
    const db = await promise
    await db.delete('pending', id)
  } catch (e) {
    console.warn('[note-recording-db] deletePendingRecording failed:', e)
  }
}
